import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import { setSavedSelectedCart, getCartListSuccess } from "../../redux/cartSlice";
import "./index.css";
import { useFirebaseAllCarts } from "../../hooks/useFirebaseCartData";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import Location from "../../assets/icons/location.PNG";
import { RESTAURANT_ID } from "../../constants/constans";
import icon from "../../assets/icons/cart.PNG";
import { Spinner } from "react-bootstrap";

const containerStyle = {
  width: "100%",
  height: "70vh",
};

const defaultCenter = {
  lat: 6.9270786,
  lng: 79.861243,
};

const libraries: ("places" | "geometry" | "marker")[] = [
  "places",
  "geometry",
  "marker"
];

export interface Locations {
  id: string;
  name: string;
  displayName?: string;
  lat: number;
  lng: number;
  imageUrl: string;
  isFixedLocation: boolean;
}

export interface MapDetailsProps {
  isMapOpen: boolean;
  toggleMap: () => void;
  instanceId?: string; // Add this to distinguish instances
}

const CartsLocationMap: React.FC<MapDetailsProps> = ({
  isMapOpen,
  toggleMap,
  instanceId = "default",
}) => {
  const dispatch = useDispatch();

  const restaurantId = RESTAURANT_ID;

  const iconURL =
    "https://storage.googleapis.com/fooddelivery-7eac6.appspot.com/Icons/cart-icon.png";

  const iconURL2 = icon;

  const [cartList, setCartList] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentLocation, setCurrentLocation] =
    useState<google.maps.LatLngLiteral | null>(null);
  
  // Use Firebase hook instead of RTK Query
  const { data: firebaseCarts, loading: isLoading, error } = useFirebaseAllCarts(restaurantId);
  const data = useSelector((state: any) => state.cartList.carts);
  
  // Dispatch Firebase cart data to Redux
  useEffect(() => {
    if (firebaseCarts && firebaseCarts.length > 0) {
      dispatch(getCartListSuccess(firebaseCarts));
    }
  }, [firebaseCarts, dispatch]);

  const [locationList, setLocationList] = useState<Locations[]>([]);

  const [selectedCart, setSelectedCart] = useState<Locations | null>(null);

  const [localSelectedCart, setLocalSelectedCart] = useState<Locations | null>(
    null
  );

  const selectedSavedCart = useSelector(
    (state: any) => state.cartList.selectedCart
  );

  // Ref to store cart item elements for scrolling
  const cartItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY! ||
      "AIzaSyD_zEfLidcypXTaAVeiQVX5PHlrcj6xEpM",
    libraries,
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      });
    }
  }, []);

  // Update cartList from Redux store (populated by Firebase hook)
  useEffect(() => {
    if (data && Array.isArray(data)) {
      setCartList(data);
    }
  }, [data]);

  useEffect(() => {
    const filteredCartList = cartList.filter(
      (cart) => cart.cartState === "Online" && cart.status === "Approved" && cart.isFixedLocation
    );

    if (cartList.length > 0) {
      const mappedLocations: Locations[] = filteredCartList.map(
        (cart: any) => ({
          id: cart.id,
          name: cart.displayName,
          lat: cart.location.latitude,
          lng: cart.location.longitude,
          imageUrl: cart.imageUrl,
          isFixedLocation: cart.isFixedLocation,
        })
      );
      setLocationList(mappedLocations);
    }
  }, [cartList, instanceId]);

  const handleCartSave = () => {
    dispatch(setSavedSelectedCart(localSelectedCart));
    toggleMap();
  };

  // const handleCartRemove = () => {
  //   dispatch(setSavedSelectedCart(null));
  //   setLocalSelectedCart(null);
  //   toggleMap();
  // };

  // Function to scroll to selected cart in the list
  const scrollToSelectedCart = (cartId: string) => {
    const cartElement = cartItemRefs.current[cartId];
    if (cartElement) {
      cartElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  // Effect to scroll when localSelectedCart changes
  useEffect(() => {
    if (localSelectedCart?.id) {
      scrollToSelectedCart(localSelectedCart.id);
    }
  }, [localSelectedCart?.id]);

  if (!isLoaded) return <div></div>;

  return (
    <>
      <Modal
        isOpen={isMapOpen}
        toggle={toggleMap}
        size="xl"
        className="container-fluid carts-location-modal"
        centered
      >
        <ModalHeader>
          <div className="d-flex flex-column text-center w-100">
            <h3>Choose Your Cart</h3>
          </div>
        </ModalHeader>

        <ModalBody className="p-3">
          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-4">
              <div className="loading-spinner mb-4">
                <Spinner
                  animation="border"
                  variant="primary"
                  style={{
                    width: '4rem',
                    height: '4rem',
                    borderWidth: '4px',
                    color: '#3f0213'
                  }}
                />
              </div>
              <h4 className="loading-title mb-3">Loading Carts...</h4>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-warning mx-3">
              <strong>Unable to load cart locations.</strong> Please check your connection.
            </div>
          )}

          {/* Two Column Layout */}
          {!isLoading && !error && (
            <div className="cart-map-layout">
              {/* Cart List Section */}
              <div className="cart-list-section">
                <div className="cart-list-header">
                  <h5>Available Pickup Locations</h5>
                  <p className="text-muted small">Select your preferred cart</p>
                </div>
                <div className="cart-list-wrapper">
                  {locationList.length > 0 ? (
                    locationList.map((cart) => (
                      <div
                        key={cart.id}
                        ref={(el) => (cartItemRefs.current[cart.id] = el)}
                        className={`cart-list-item ${
                          (localSelectedCart?.id || selectedSavedCart?.id) === cart.id
                            ? 'selected'
                            : ''
                        }`}
                        onClick={() => {
                          setLocalSelectedCart(cart);
                        }}
                      >
                        <div className="cart-item-content">
                          <div className="cart-radio">
                            {(localSelectedCart?.id || selectedSavedCart?.id) === cart.id ? (
                              <div className="radio-selected">
                                <span>✓</span>
                              </div>
                            ) : (
                              <div className="radio-unselected"></div>
                            )}
                          </div>
                          <div className="cart-details">
                            <h6 className="cart-name">{cart.name}</h6>
                            <p className="cart-type">
                              {cart.isFixedLocation ? 'Fixed Location' : 'Mobile Cart'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-carts-message">
                      <p>No pickup locations available at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Map Section */}
              <div className="map-section">
                <div
                  className="p-1 border map-bg-color rounded"
                  style={{ height: "100%", width: "100%" }}
                >
            {/* <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}> */}
            <GoogleMap
              mapContainerStyle={containerStyle}
              // center={currentLocation || defaultCenter}
              center={defaultCenter}
              zoom={14}
              options={{
                disableDefaultUI: false,
                clickableIcons: false,
                styles: []
              }}
            >
              {/* {currentLocation && (
                <Marker position={currentLocation} label="You" />
              )} */}
              {locationList.length > 0 &&
                locationList.map((cart: any, index: number) => (
                  <Marker
                    key={cart.id}
                    position={{
                      lat: cart.lat,
                      lng: cart.lng,
                    }}
                    icon={{
                      url: cart.isFixedLocation ? iconURL : iconURL2,
                      scaledSize: new window.google.maps.Size(40, 40),
                    }}
                    onClick={() => {
                      setLocalSelectedCart(cart);
                      // dispatch(setSavedSelectedCart(localSelectedCart));
                    }}
                    onMouseOver={() => {
                      setSelectedCart(cart);
                    }}
                  />
                ))}

              {selectedCart === null ? (
                <div></div>
              ) : (
                <InfoWindow
                  position={{ lat: selectedCart.lat, lng: selectedCart.lng }}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -40),
                  }}
                >
                  <div style={{ padding: "10px", maxWidth: "250px" }}>
                    <h5 style={{ marginBottom: "5px" }}>{selectedCart.name}</h5>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
                </div>
              </div>
            </div>
          )}

          {/* Selected Cart Info */}
          {!isLoading && !error && (
            <div className="cart-selection-info mt-3">
              <div className="location-icon-container">
                <img
                  src={Location}
                  alt="Location Icon"
                  className="location-icon"
                />
              </div>
              <span>
                {
                  selectedSavedCart || localSelectedCart
                    ? `You have selected ${selectedSavedCart?.displayName || selectedSavedCart?.name }`
                    : "Please select your preferred cart from the list or map"
                }
              </span>
            </div>
          )}

          {/* Action Buttons */}
          {!isLoading && !error && (
            <div className="button-container mt-3">
              <button className="model-close-button" onClick={toggleMap}>
                Close
              </button>
              <button
                className={`confirm-location-button ${
                  !localSelectedCart?.id ? "disabled-button" : ""
                }`}
                onClick={handleCartSave}
                disabled={!localSelectedCart?.id}
              >
                Confirm Location
              </button>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default CartsLocationMap;
